import React, { ChangeEvent, ReactNode, useMemo, useState } from "react";
import { SectionTitle } from "./SectionTitle";
import { ZipCodeField } from "./ZipcodeField";
import {
    Box,
    Button,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import { CheckoutFormTypeFields, useCheckoutForm } from "./hooks/useCheckoutForm";
import { OrderDeliveryAddressFields, OrderShippingViewDTOFields } from "../../types/purchaseOrder/Order";
import { EntityWithIdFields } from "../../types/baseEntities";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";

import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";

import { BranchPickerDialog, BranchForPickup } from "../Shipping/BranchPickerDialog";
import { SHIPPING_ACTION_LINK_SX } from "../../styles/shippingActionLinkStyles";
import { mapsUrlBuilder } from "@ecommerce/shared-utils";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";

const makeDeliveryValue = (shippingOptionId: string, branchId: string | null) =>
    `${shippingOptionId}::${branchId ?? ""}`;

const parseDeliveryValue = (value?: string | null) => {
    if (!value) return { optionId: null as string | null, branchId: null as string | null };
    const parts = String(value).split("::");
    const optionId = parts[0] || null;
    const branchId = parts.length > 1 && parts[1] ? parts[1] : null;
    return { optionId, branchId };
};

const splitTitle = (t: string) => {
    const parts = String(t ?? "").split(" - ");
    if (parts.length >= 2) {
        return {
            optionTitle: parts[0],
            branchTitle: parts.slice(1).join(" - "),
        };
    }
    return { optionTitle: String(t ?? ""), branchTitle: "" };
};

const getBranchId = (b: any, idx: number) =>
    String(
        b?.id ??
        b?.[EntityWithIdFields.Id] ??
        b?.uniIdSucursal ??
        b?.idSucursal ??
        idx
    );

const normalizeBranchForDialog = (b: any, idx: number): BranchForPickup => {
    const id = getBranchId(b, idx);

    return {
        ...b,
        id,
        nombre: b?.nombre ?? b?.name ?? b?.descripcion ?? b?.desc ?? "Sucursal",
        direccion: b?.direccion ?? b?.address ?? b?.domicilio ?? b?.calle ?? null,
        codigoPostal: b?.codigoPostal ?? b?.cp ?? b?.postalCode ?? null,
        latitud: b?.latitud ?? b?.lat ?? b?.latitude ?? null,
        longitud: b?.longitud ?? b?.lng ?? b?.longitude ?? null,
    };
};

type PickupGroupedUI = {
    optionId: string;
    optionTitle: string;
    price: string;
    fallbackDescription: string;
};

export const DeliverySection: React.FC = () => {
    const {
        zipCode,
        confirmedZipCode,
        onCancelZipCode,
        deliveryOptions,
        shippingOptions,
        methods,
        setDeliveryType,
    } = useCheckoutForm();

    const baseName = CheckoutFormTypeFields.DeliveryAddress;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    React.useEffect(() => {
        methods.register(CheckoutFormTypeFields.DeliveryType, {
            required: "Debes seleccionar un tipo de entrega",
        });
    }, [methods]);

    const deliveryType = methods.watch(CheckoutFormTypeFields.DeliveryType) as string | undefined;
    const { optionId: selectedOptionId, branchId: selectedBranchId } = useMemo(
        () => parseDeliveryValue(deliveryType),
        [deliveryType]
    );

    const pickupWithBranchesIds = useMemo(() => {
        const set = new Set<string>();
        (shippingOptions ?? []).forEach((opt: any) => {
            const optionId = String(opt?.[EntityWithIdFields.Id] ?? "");
            const isPickup = opt?.[OrderShippingViewDTOFields.RequestAddress] === false;
            const branches = (opt?.[OrderShippingViewDTOFields.Branches] ?? []) as any[];
            if (optionId && isPickup && branches.length > 0) set.add(optionId);
        });
        return set;
    }, [shippingOptions]);

    const pickupGrouped = useMemo<PickupGroupedUI[]>(() => {
        const pickupGroup =
            (deliveryOptions ?? []).find((g: any) => {
                const title = String(g?.title ?? "").toLowerCase();
                const hasBranchValues = (g?.options ?? []).some((o: any) => parseDeliveryValue(o?.value)?.branchId);
                return title.includes("retirar") || hasBranchValues;
            }) ?? null;

        if (!pickupGroup) return [];

        const byOptionId = new Map<string, any[]>();
        (pickupGroup.options ?? []).forEach((opt: any) => {
            const { optionId } = parseDeliveryValue(opt?.value);
            if (!optionId) return;
            if (!byOptionId.has(optionId)) byOptionId.set(optionId, []);
            byOptionId.get(optionId)!.push(opt);
        });

        const result: PickupGroupedUI[] = [];
        byOptionId.forEach((items, optionId) => {
            const first = items[0] ?? {};
            const firstTitle = String(first.title ?? "");
            const { optionTitle } = splitTitle(firstTitle);

            result.push({
                optionId,
                optionTitle: pickupWithBranchesIds.has(optionId) ? optionTitle : firstTitle,
                price: String(first.price ?? ""),
                fallbackDescription: typeof first.description === "string" ? first.description : "",
            });
        });

        return result;
    }, [deliveryOptions, pickupWithBranchesIds]);
    
    const radioGroupValue = useMemo(() => {
        if (!deliveryType) return "";
        const { optionId, branchId } = parseDeliveryValue(deliveryType);
        if (!optionId) return "";

        if (pickupWithBranchesIds.has(optionId) && !branchId) return "";
        return makeDeliveryValue(optionId, branchId);
    }, [deliveryType, pickupWithBranchesIds]);

    const [branchDialogOpen, setBranchDialogOpen] = useState(false);
    const [branchDialogOptionId, setBranchDialogOptionId] = useState<string | null>(null);

    const openBranchDialog = (optionId: string) => {
        setBranchDialogOptionId(optionId);
        setBranchDialogOpen(true);
    };

    const closeBranchDialog = () => {
        setBranchDialogOpen(false);
        setBranchDialogOptionId(null);
    };

    const getOptionById = (optionId: string) =>
        (shippingOptions ?? []).find((o: any) => String(o?.[EntityWithIdFields.Id]) === String(optionId));

    const getBranchesForOption = (optionId: string): BranchForPickup[] => {
        const opt = getOptionById(optionId);
        const raw = (opt?.[OrderShippingViewDTOFields.Branches] ?? []) as any[];
        return raw.map((b, idx) => normalizeBranchForDialog(b, idx));
    };

    const onHandleChangeDelivery = (e: ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        const { optionId, branchId } = parseDeliveryValue(nextValue);

        if (!optionId) return;

        if (pickupWithBranchesIds.has(optionId) && !branchId) {
            openBranchDialog(optionId);
            return;
        }

        setDeliveryType(nextValue);
    };

    const dialogOption = branchDialogOptionId ? getOptionById(branchDialogOptionId) : null;
    const dialogBranches = branchDialogOptionId ? getBranchesForOption(branchDialogOptionId) : [];
    const dialogProviderName = (dialogOption?.[OrderShippingViewDTOFields.ProviderName] as string | undefined) ?? "";
    const dialogOptionName = (dialogOption?.[OrderShippingViewDTOFields.OptionName] as string | undefined) ?? undefined;

    const dialogSelectedBranchId =
        branchDialogOptionId && branchDialogOptionId === selectedOptionId ? selectedBranchId : null;

    return (
        <Stack mb={confirmedZipCode ? "auto" : 2}>
            <Stack sx={{ position: "relative" }}>
                <SectionTitle>Entrega</SectionTitle>
                {confirmedZipCode && (
                    <Box
                        sx={{
                            position: !isMobile ? "absolute" : "",
                            alignSelf: isMobile ? "center" : "",
                            right: 0,
                            top: 0,
                        }}
                    >
                        <Button variant="outlined" sx={BUTTON_STYLES.grey} onClick={onCancelZipCode}>
                            {`CAMBIAR CP ${zipCode}`}
                        </Button>
                    </Box>
                )}
            </Stack>

            {!confirmedZipCode && (
                <ZipCodeField
                    control={methods.control}
                    name={`${baseName}.${OrderDeliveryAddressFields.ZipCode}`}
                />
            )}

            {confirmedZipCode && (
                <>
                    <RadioGroup value={radioGroupValue} onChange={onHandleChangeDelivery}>
                        <Stack spacing={2.5}>
                            {(deliveryOptions ?? []).map((deliveryOption: any, groupIdx: number) => {
                                const title = String(deliveryOption?.title ?? "");
                                const lower = title.toLowerCase();
                                const isPickupGroup =
                                    lower.includes("retirar") ||
                                    (deliveryOption?.options ?? []).some((o: any) => parseDeliveryValue(o?.value)?.branchId);

                                return (
                                    <Stack key={`${title}-${groupIdx}`} spacing={0.5}>
                                        <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
                                            <deliveryOption.Icon color={"rgba(1,1,1,.6)"} />
                                            <Typography color={"rgba(1,1,1,.6)"} sx={TYPOGRAPHY_STYLES.rubikSmall}>
                                                {deliveryOption.title}
                                            </Typography>
                                        </Stack>

                                        <Stack>
                                            {/* Grupo normal (domicilio, etc.) */}
                                            {!isPickupGroup &&
                                                (deliveryOption.options ?? []).map((a: any, idx: number) => (
                                                    <DeliverySectionOption
                                                        key={`${a.value}-${idx}`}
                                                        value={a.value}
                                                        title={a.title}
                                                        description={a.description}
                                                        price={a.price}
                                                    />
                                                ))}

                                            {/* Grupo pickup agrupado */}
                                            {isPickupGroup &&
                                                pickupGrouped.map((g) => {
                                                    const hasBranches = pickupWithBranchesIds.has(g.optionId);

                                                    if (!hasBranches) {
                                                        return (
                                                            <DeliverySectionOption
                                                                key={g.optionId}
                                                                value={makeDeliveryValue(g.optionId, null)}
                                                                title={g.optionTitle}
                                                                description={g.fallbackDescription}
                                                                price={g.price}
                                                            />
                                                        );
                                                    }

                                                    // Caso 2: pickup CON sucursales => 1 sola card + dialog
                                                    const isSelectedThisOption = selectedOptionId === g.optionId && !!selectedBranchId;

                                                    const radioValue = makeDeliveryValue(
                                                        g.optionId,
                                                        isSelectedThisOption ? selectedBranchId : null
                                                    );

                                                    const opt = getOptionById(g.optionId);
                                                    const providerName =
                                                        (opt?.[OrderShippingViewDTOFields.ProviderName] as string | undefined) ?? "";

                                                    const branches = getBranchesForOption(g.optionId);
                                                    const selectedBranch =
                                                        isSelectedThisOption && selectedBranchId
                                                            ? branches.find((b) => String(b.id) === String(selectedBranchId)) ?? null
                                                            : null;

                                                    const mapsUrl = selectedBranch
                                                        ? mapsUrlBuilder.buildBranchUrl(providerName, selectedBranch)
                                                        : null;

                                                    return (
                                                        <PickupWithBranchesOption
                                                            key={g.optionId}
                                                            value={radioValue}
                                                            optionTitle={g.optionTitle}
                                                            selectedBranchName={(selectedBranch?.nombre as string | null) ?? null}
                                                            selectedBranchAddress={(selectedBranch?.direccion as string | null) ?? null}
                                                            selectedBranchMapsUrl={mapsUrl}
                                                            price={g.price}
                                                            onOpenDialog={(ev) => {
                                                                ev.preventDefault();
                                                                ev.stopPropagation();
                                                                openBranchDialog(g.optionId);
                                                            }}
                                                        />
                                                    );
                                                })}
                                        </Stack>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </RadioGroup>

                    {methods.formState.errors.deliveryType && (
                        <Typography color="error" fontSize={12} mt={0.5}>
                            {methods.formState.errors.deliveryType.message}
                        </Typography>
                    )}
                </>
            )}

            {/* Dialog selección de sucursal (reutilizamos BranchPickerDialog) */}
            <BranchPickerDialog
                open={branchDialogOpen}
                onClose={closeBranchDialog}
                optionName={dialogOptionName}
                providerName={dialogProviderName}
                branches={dialogBranches}
                selectedBranchId={dialogSelectedBranchId}
                onSelect={(branchId) => {
                    if (!branchDialogOptionId) return;
                    setDeliveryType(makeDeliveryValue(branchDialogOptionId, branchId));
                    closeBranchDialog();
                }}
                searchPlaceholder="Buscar por nombre, dirección o CP"
            />
        </Stack>
    );
};

interface DeliverySectionOptionProps {
    value: any;
    title: string;
    description: string | ReactNode;
    price: string;
}

const DeliverySectionOption: React.FC<DeliverySectionOptionProps> = ({
    value,
    title,
    description,
    price,
}: DeliverySectionOptionProps) => {
    return (
        <Box sx={{ border: "1px solid #f0f0f0", mb: 1, p: 1 }}>
            <FormControlLabel
                value={value}
                control={<Radio />}
                sx={{ width: "100%", "& > .MuiFormControlLabel-label": { width: "100%" } }}
                label={
                    <Stack direction={"row"} justifyContent={"space-between"} width={1} paddingY={1} alignItems="center">
                        <Stack>
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ color: "#1b1b1b", fontSize: "14px", fontFamily: "Rubik" }}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ color: "#1b1b1b", opacity: 0.8, fontSize: "13px", fontFamily: "Rubik" }}
                            >
                                {description}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="h4"
                            fontWeight={600}
                            sx={{ color: STYLES.colors.secondary, fontSize: "16px", fontFamily: "Rubik" }}
                        >
                            {price}
                        </Typography>
                    </Stack>
                }
            />
        </Box>
    );
};

interface PickupWithBranchesOptionProps {
    value: string; // optionId::branchId o optionId::
    optionTitle: string;
    selectedBranchName: string | null;
    selectedBranchAddress: string | null;
    selectedBranchMapsUrl: string | null;
    price: string;
    onOpenDialog: (e: React.MouseEvent) => void;
}

const PickupWithBranchesOption: React.FC<PickupWithBranchesOptionProps> = ({
    value,
    optionTitle,
    selectedBranchName,
    selectedBranchAddress,
    selectedBranchMapsUrl,
    price,
    onOpenDialog,
}) => {
    const hasSelectedBranch = !!selectedBranchName;

    return (
        <Box sx={{ border: "1px solid #f0f0f0", mb: 1, p: 1 }}>
            <FormControlLabel
                value={value}
                control={<Radio />}
                sx={{ width: "100%", m: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { width: "100%" } }}
                label={
                    <Stack direction={"row"} justifyContent={"space-between"} width={1} paddingY={1} alignItems="flex-start">
                        <Stack sx={{ flex: 1, pr: 2 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ color: "#1b1b1b", fontSize: "14px", fontFamily: "Rubik" }}
                            >
                                {optionTitle}
                                {selectedBranchName ? ` - ${selectedBranchName}` : ""}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mt: 0.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        color: "#1b1b1b",
                                        opacity: 0.8,
                                        fontSize: "13px",
                                        fontFamily: "Rubik",
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {selectedBranchAddress ? selectedBranchAddress : "Seleccioná una sucursal para retiro"}
                                </Typography>

                                {selectedBranchMapsUrl && (
                                    <Button
                                        component="a"
                                        href={selectedBranchMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<RoomOutlinedIcon sx={{ fontSize: 16 }} />}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            ...SHIPPING_ACTION_LINK_SX,
                                            mt: 0,
                                            flexShrink: 0,
                                        }}
                                    >
                                        Ver en mapa
                                    </Button>
                                )}
                            </Box>

                            <Button
                                variant="text"
                                startIcon={
                                    hasSelectedBranch
                                        ? <SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />
                                        : <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onOpenDialog(e);
                                }}
                                sx={{ ...SHIPPING_ACTION_LINK_SX, mt: 1 }}
                            >
                                {hasSelectedBranch ? "Cambiar sucursal" : "Elegir sucursal"}
                            </Button>
                        </Stack>

                        <Typography
                            variant="h4"
                            fontWeight={600}
                            sx={{ color: STYLES.colors.secondary, fontSize: "16px", fontFamily: "Rubik", whiteSpace: "nowrap" }}
                        >
                            {price}
                        </Typography>
                    </Stack>
                }
            />
        </Box>
    );
};














